import type { Guide, GuideRecordHint } from '../types';

/**
 * S-22.10-03 — korekty kartoteki pojazdu (makieta FTL p. Mariusza, procedura FTL-08; scenariusz e2e 25).
 * DEMO 025 z `e2e/setup/seed-e2e.ts` ma już korekty naczepy i terminu przeglądu („≠ TMS”) i wariant
 * DEMO/2026/10/K01 — okno „Zmień kierowcę” pokazuje go w skutkach. Nagrywarka tylko otwiera okno,
 * niczego nie zapisuje.
 */
const CAR: GuideRecordHint = {
  description: 'pojazd, którego jesteś opiekunem FTL (planista główny — dowolny)',
  listPath: '/cars',
};

export const guide: Guide = {
  flagKey: 'fleetCorrections',
  title: 'Korekty kartoteki pojazdu: kierowca, naczepa, terminy',
  whatItDoes:
    'Gdy TMS jest nieaktualny, opiekun pojazdu albo planista główny poprawia na karcie pojazdu kierowcę, naczepę i terminy uprawnień — z powodem. Wartość różna od TMS ma znacznik „≠ TMS”: w dymku widać, kto i kiedy ją zmienił, dlaczego i jaka jest w TMS; „przywróć z TMS” cofa korektę. Rekomendacje, ładowność zestawu i uprawnienia liczą z wartości po korekcie. Pojazd bez kierowcy wypada z rekomendacji, a jego warianty wygasają — okno pokazuje je przed zapisem. Import z TMS korekty nie nadpisuje: gdy TMS ma już tę samą wartość, korekta zamyka się sama.',
  audience: ['planista FTL (opiekun pojazdu)', 'Team Leader (planista główny)', 'administrator'],
  presentationItems: ['S-22.10-03'],
  steps: [
    {
      route: '/cars/:carId/show',
      record: CAR,
      target: 'car-corrections',
      title: 'Sekcja „Kierowca i zestaw”',
      description:
        'Na karcie pojazdu, obok uprawnień, jest kierowca i naczepa z przyciskiem „Zmień”. Przy cudzym aucie planisty przycisk jest wyszarzony, a dymek mówi, kto może korygować.',
      action: 'Otwórz pojazd z listy „Samochody” i zjedź do sekcji „Kierowca i zestaw”.',
      recorder: { account: 'ftl', demo: { kind: 'car', plate: 'DEMO 025' } },
    },
    {
      route: '/cars/:carId/show',
      record: CAR,
      target: 'car-correction-mark-trailer',
      title: 'Znacznik „≠ TMS” i „przywróć z TMS”',
      description:
        'Wartość poprawiona w aplikacji ma znacznik „≠ TMS”. W dymku: kto i kiedy ją zmienił, powód i wartość w TMS. Gdy TMS zmieni się od tej pory inaczej, pod wartością pojawia się „TMS ma teraz …”. „Przywróć z TMS” cofa korektę.',
      action: 'Najedź na „≠ TMS” przy naczepie.',
      recorder: { account: 'ftl', demo: { kind: 'car', plate: 'DEMO 025' } },
    },
    {
      route: '/cars/:carId/show',
      record: CAR,
      target: 'car-correction-effects',
      title: 'Zmiana kierowcy — skutki przed zapisem',
      description:
        'Okno pokazuje, co się stanie: bez kierowcy pojazd wypada z rekomendacji, a jego warianty wygasają (zlecenia z autorami wariantów — dostaną powiadomienie), oraz obsadzenia auta. Powód jest obowiązkowy i zostaje w historii pojazdu.',
      action: 'Kliknij „Zmień” przy kierowcy, wybierz kierowcę albo „bez kierowcy” i wpisz powód.',
      recorder: {
        account: 'ftl',
        demo: { kind: 'car', plate: 'DEMO 025' },
        actions: [{ kind: 'click', target: 'car-correction-edit-driver' }],
      },
    },
    {
      route: '/cars/:carId/show',
      record: CAR,
      target: 'car-eligibility-edit-*',
      title: '„Zmień termin” przy uprawnieniach',
      description:
        'Termin przeglądu czy legalizacji poprawia się tak samo — z datą i powodem. Rekomendacje liczą z nowego terminu: nieważne uprawnienie typu Blokada wyklucza pojazd, Alert ostrzega.',
      action: 'W „Uprawnieniach i alertach z kartoteki TMS” kliknij „Zmień termin” przy wpisie.',
      recorder: { account: 'ftl', demo: { kind: 'car', plate: 'DEMO 025' } },
    },
    {
      route: '/cars/:carId/show',
      record: CAR,
      target: 'car-todo',
      title: '„Do zrobienia”: kartoteka różni się od TMS',
      description:
        'Panel „Do zrobienia” przypomina, że pojazd ma wartości skorygowane w aplikacji, i ostrzega, gdy TMS zmienił się od korekty.',
      action: 'Spójrz na panel „Do zrobienia” pod podsumowaniem karty.',
      recorder: { account: 'ftl', demo: { kind: 'car', plate: 'DEMO 025' } },
    },
    {
      route: '/cars',
      target: 'fleet-correction-marker',
      title: 'Lista pojazdów: znacznik „≠ TMS”',
      description: 'Na liście pojazdów auto z korektą kartoteki ma „≠ TMS” przy kierowcy.',
      action: 'Otwórz listę „Samochody” i znajdź znacznik „≠ TMS”.',
      recorder: { account: 'ftl' },
    },
  ],
};
